import { DateTimePicker12 } from './TimePicker12';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { assignVisitAgent, getVisitAgents } from '../api';

// 03_requirements_missing.md story 4 - the owner (or admin) assigns a site-visit agent (a
// `delivery`-role account) to a confirmed visit. Adapted from jewelry's AssignDeliveryModal:
// the property's area is passed so whoever covers it sorts first, and the visit date is passed
// so anyone with an overlapping unavailability window is flagged.
export default function AssignAgentModal({ visit, areaHint = '', show, onHide, onAssigned }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentId, setAgentId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !visit) return;
    setError('');
    setAgentId(visit.deliveryAssignment?.assignedTo?._id || visit.deliveryAssignment?.assignedTo || '');
    setScheduledAt(visit.deliveryAssignment?.scheduledAt ? String(visit.deliveryAssignment.scheduledAt).slice(0, 16)
      : (visit.startDate ? new Date(visit.startDate).toISOString().slice(0, 16) : ''));
    setNotes(visit.deliveryAssignment?.notes || '');
    setLoading(true);
    getVisitAgents(areaHint, visit.deliveryAssignment?.scheduledAt || visit.startDate || '')
      .then(setAgents)
      .catch(() => setError('Could not load agents.'))
      .finally(() => setLoading(false));
  }, [show, visit, areaHint]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agentId) { setError('Please choose an agent.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const result = await assignVisitAgent(visit._id, {
        agentId,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        notes: notes.trim()
      });
      onAssigned(result);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to assign an agent.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visit) return null;
  const selected = agents.find((a) => a._id === agentId);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Assign a site-visit agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-2">
            The agent shows the property to the visitor. They'll see the site address and the
            visitor's contact on their own dashboard.
          </div>
          {loading && <Spinner animation="border" size="sm" role="status" />}
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          {!loading && agents.length === 0 && (
            <Alert variant="light" className="py-2">
              No agent accounts exist yet — request one from the Agents tab, or ask an admin to add one.
            </Alert>
          )}
          <Form.Group className="mb-2" controlId="assign-agent-person">
            <Form.Label>Agent</Form.Label>
            <Form.Select value={agentId} onChange={(e) => setAgentId(e.target.value)} required>
              <option value="">Choose…</option>
              {agents.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name} {a.phone ? `(${a.phone})` : ''}
                  {a.matchesArea ? ' — serves this area' : ''}
                  {a.available === false ? ' — may be unavailable' : ''}
                </option>
              ))}
            </Form.Select>
            {selected && (
              <div className="mt-1">
                {selected.matchesArea && <Badge bg="success" className="me-1">Serves this area</Badge>}
                {selected.available === false && <Badge bg="warning" text="dark">May be unavailable on this date</Badge>}
              </div>
            )}
          </Form.Group>
          <Form.Group className="mb-2" controlId="assign-agent-schedule">
            <Form.Label>Visit date &amp; time</Form.Label>
            <DateTimePicker12  value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2" controlId="assign-agent-notes">
            <Form.Label className="small">Notes for the agent (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Assigning…' : 'Assign agent'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
